// API client backend PHP/SQLite su Aruba
// Usa sempre index.php?route=... per evitare dipendenza da mod_rewrite (compatibile Hosting Basic Aruba)

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api/index.php` : '/api/index.php');

function getToken() {
  try {
    const raw = localStorage.getItem('adminAuth');
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data.token || null;
  } catch {
    return null;
  }
}

function buildUrl(path) {
  // Path può contenere già query string (es. "messages?id=5")
  const sep = path.includes('?') ? '&' : '?';
  return `${API_BASE}${API_BASE.includes('?') ? '&' : '?'}route=${path.replace(/^\//, '').replace('?', sep)}`;
}

async function request(path, { method = 'GET', body, auth = false, isFormData = false } = {}) {
  const headers = {};
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  // Cache-buster sui GET per evitare cache di Aruba/Cloudflare/browser
  let url = buildUrl(path);
  if (method === 'GET') url += `&_=${Date.now()}`;

  const res = await fetch(url, {
    method,
    headers,
    cache: 'no-store',
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try { data = await res.json(); } catch { /* no-op */ }
  if (!res.ok) {
    // Sessione scaduta o token mancante: pulisci e rimanda al login
    if (res.status === 401 && auth) {
      try { localStorage.removeItem('adminAuth'); } catch { /* no-op */ }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/organizzazione')) {
        window.location.href = '/admin';
      } else if (typeof window !== 'undefined' && window.location.pathname === '/admin/dashboard') {
        window.location.href = '/admin';
      }
    }
    const err = new Error(data?.error || `Errore ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return normalizeMediaUrls(data);
}

/**
 * Walk ricorsivamente la risposta API: ogni stringa che inizia con "uploads/"
 * o "api/uploads/" (URL relativo legacy) viene normalizzata a "/api/uploads/...".
 * Necessario perché i file fisici stanno in /api/uploads/ ma alcuni endpoint
 * PHP salvavano il path SENZA prefisso /api/ né leading slash, causando 404
 * quando il browser risolveva l'URL relativo alla pagina corrente.
 */
function normalizeMediaUrls(value) {
  if (value == null) return value;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return value;
    // URL assoluto che contiene /api/uploads/ (dominio "sbagliato" tipo vecchio preview
    // o vecchio dominio) → estrai solo il path per renderlo portabile.
    const absMatch = /^(https?:)?\/\/[^/]+(\/api\/uploads\/.+)$/i.exec(s);
    if (absMatch) return absMatch[2];
    if (/^(https?:)?\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('/')) return value;
    if (s.startsWith('uploads/')) return '/api/' + s;
    if (s.startsWith('api/uploads/')) return '/' + s;
    return value;
  }
  if (Array.isArray(value)) return value.map(normalizeMediaUrls);
  if (typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = normalizeMediaUrls(value[k]);
    return out;
  }
  return value;
}

export const api = {
  login: (username, password) => request('auth', { method: 'POST', body: { username, password } }),
  verifyAuth: () => request('auth/me', { auth: true }),
  getSettings: () => request('settings'),
  updateSettings: (data) => request('settings', { method: 'PUT', body: data, auth: true }),
  sendMessage: (data) => request('messages', { method: 'POST', body: data }),
  listMessages: () => request('messages', { auth: true }),
  deleteMessage: (id) => request(`messages?id=${id}`, { method: 'DELETE', auth: true }),
  listTeams: (categoria) => request(categoria ? `teams?categoria=${encodeURIComponent(categoria)}` : 'teams'),
  saveTeam: (data) => request('teams', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deleteTeam: (id) => request(`teams?id=${id}`, { method: 'DELETE', auth: true }),
  getClassifiche: () => request('classifiche'),
  updateClassifica: (data) => request('classifiche', { method: 'PUT', body: data, auth: true }),
  saveClassifica: (data) => request('classifiche', { method: 'PUT', body: data, auth: true }),
  deleteClassifica: (categoria) => request(`classifiche&categoria=${encodeURIComponent(categoria)}`, { method: 'DELETE', auth: true }),
  getCalendario: () => request('calendario'),
  getRegolamento: () => request('regolamento'),
  getPartners: () => request('partners'),
  getGallery: () => request('gallery'),
  addGallery: (data) => request('gallery', { method: 'POST', body: data, auth: true }),
  updateGallery: (data) => request('gallery', { method: 'PUT', body: data, auth: true }),
  deleteGallery: (id) => request(`gallery?id=${id}`, { method: 'DELETE', auth: true }),
  getVideos: () => request('videos'),
  addVideo: (data) => request('videos', { method: 'POST', body: data, auth: true }),
  updateVideo: (data) => request('videos', { method: 'PUT', body: data, auth: true }),
  deleteVideo: (id) => request(`videos?id=${id}`, { method: 'DELETE', auth: true }),
  // Partners
  savePartnerSection: (data) => request('partners/section', { method: data.exists ? 'PUT' : 'POST', body: data, auth: true }),
  deletePartnerSection: (id) => request(`partners/section?id=${id}`, { method: 'DELETE', auth: true }),
  savePartnerItem: (data) => request('partners/item', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deletePartnerItem: (id) => request(`partners/item?id=${id}`, { method: 'DELETE', auth: true }),
  getLayout: (page) => request(`layouts?page=${encodeURIComponent(page)}`),
  saveLayout: (page, blocks) => request('layouts', { method: 'POST', body: { page, blocks }, auth: true }),
  // Media library
  listMedia: () => request('media'),
  deleteMedia: (rel) => request(`media?rel=${encodeURIComponent(rel)}`, { method: 'DELETE', auth: true }),
  // Account
  changePassword: (current_password, new_password) => request('account', { method: 'POST', body: { current_password, new_password }, auth: true }),
  // Campi
  getCampi: () => request('campi'),
  saveCampo: (data) => request('campi', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deleteCampo: (id) => request(`campi?id=${id}`, { method: 'DELETE', auth: true }),
  // Upload file (multipart)
  uploadFile: async (file, folder = 'misc') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', folder);
    return request('upload', { method: 'POST', body: fd, auth: true, isFormData: true });
  },
  getEditions: () => request('editions'),
  saveEdition: (data) => request('editions', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deleteEdition: (id) => request(`editions?id=${id}`, { method: 'DELETE', auth: true }),
  getRegolamentoFull: () => request('regolamento'),
  saveRegolamento: (data) => request('regolamento', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deleteRegolamento: (id) => request(`regolamento?id=${id}`, { method: 'DELETE', auth: true }),
  getCalendarioFull: () => request('calendario'),
  saveCalendario: (data) => request('calendario', { method: data.id ? 'PUT' : 'POST', body: data, auth: true }),
  deleteCalendario: (id) => request(`calendario?id=${id}`, { method: 'DELETE', auth: true }),
  getChiSiamo: () => request('chi-siamo'),
  saveChiSiamo: (data) => request('chi-siamo', { method: 'POST', body: data, auth: true }),
  getBrochure: () => request('brochure'),
  saveBrochure: (data) => request('brochure', { method: 'POST', body: data, auth: true }),
  getContatti: () => request('contatti'),
  saveContatti: (data) => request('contatti', { method: 'POST', body: data, auth: true }),
  getFinale: () => request('finale'),
  saveFinale: (data) => request('finale', { method: 'POST', body: data, auth: true }),
  getConcorso: () => request('concorso'),
  saveConcorso: (data) => request('concorso', { method: 'POST', body: data, auth: true }),
  getTournament: () => request('tournament'),
  saveTournament: (data) => request('tournament', { method: 'POST', body: data, auth: true }),
  listPages: () => request('pages'),
  getPageBySlug: (slug) => request(`pages?slug=${encodeURIComponent(slug)}`),
  createPage: (data) => request('pages', { method: 'POST', body: data, auth: true }),
  updatePage: (data) => request('pages', { method: 'PUT', body: data, auth: true }),
  deletePage: (id) => request(`pages?id=${id}`, { method: 'DELETE', auth: true }),
  upload: async (file, folder = 'misc') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', folder);
    return request('upload', { method: 'POST', body: fd, auth: true, isFormData: true });
  },
};

export async function isBackendOnline() {
  try {
    const s = await request('settings');
    // Se l'install non è stato fatto, settings è vuoto -> consideriamo non pronto
    return s && Object.keys(s).length > 0;
  } catch {
    return false;
  }
}
